import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';

import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY, MUTATION_CART_UPDATE_LINE_ITEMS_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useCart } from '$src/hooks/queries';
import { gateway } from '$src/http';
import { UpdateLineItemsInput } from '$src/pages/api/[shopId]/[cartId]/update-line-items';

export const useUpdateLineItems = () => {
  const baseUrl = useBaseApiUrl();
  const toast = useToast();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const updateCartId = useCartIdUpdater();
  const { data: cart } = useCart();

  return useMutation({
    mutationFn: (data: UpdateLineItemsInput) => gateway.post<Cart>(`${baseUrl}/update-line-items`, data),
    mutationKey: [MUTATION_CART_UPDATE_LINE_ITEMS_KEY, baseUrl],
    onMutate: async ({ lineItems }) => {
      // Optimistically update quantities
      const previousCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, cart?.id]);
      const newLineItems =
        previousCart?.lineItems
          ?.map((lineItem) => {
            const lineItemToUpdate = lineItems.find((item) => item.id === lineItem.id);
            if (!lineItemToUpdate) return lineItem;
            if (lineItemToUpdate.quantity === 0) return null;
            return { ...lineItem, quantity: lineItemToUpdate.quantity };
          })
          .filter(Boolean) ?? [];
      const newCart = { ...previousCart, lineItems: newLineItems };
      // @ts-expect-error - known
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, newCart.id], newCart);

      return { previousCart };
    },
    onSuccess: ({ data }) => {
      updateCartId(data.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
    },
    onError: (err, data, context) => {
      // If the mutation fails, roll back to the previous cart state
      toast({
        description: t('checkout_form.error.lineItemsUpdateError'),
        status: 'error',
        position: 'top',
        isClosable: true,
        duration: 5000,
      });
      if (!context?.previousCart) return;
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, context?.previousCart?.id], context?.previousCart);
    },
  });
};
